import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import useReceiptPrinter from '../../utils/ReceiptPrint';
import { displayDate, displayDateTime } from '../../utils/CommonUtils';

import Card from '../common/Card';
import EmptyState from '../common/EmptyState';
import Money from '../common/Money';
import SummaryList, { SummaryRow } from '../common/SummaryList';

/**
 * Деталі акту продажу.
 *
 * Було: ідентифікатор акту брався тільки з location.state, тому пряме
 * відкриття або перезавантаження сторінки /salehistory/sale/123 падало
 * з TypeError. Тепер id читається з параметра маршруту.
 */
const SaleDetails = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef(null);

    const authenticatedRequest = useAuthenticatedRequest();
    const printReceipt = useReceiptPrinter();

    const actId = params.id ?? location.state?.id;

    const [act, setAct] = useState(null);
    const [products, setProducts] = useState([]);
    const [charged, setCharged] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!actId) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;

        const resolveClientName = async (cagentId) => {
            const response = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}enums/clients/`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );

            if (response?.status !== 200) {
                return null;
            }

            const parsed = await response.json();
            // Було: `client.name` без перевірки — якщо клієнта видалили,
            // сторінка падала замість того, щоб показати акт.
            return parsed.find((client) => client.id === cagentId)?.name ?? null;
        };

        const fetchPayments = async (id) => {
            const response = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}accounting_payments/get_payments_by_act/?act_id=${id}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );

            if (response?.status === 200) {
                const parsed = await response.json();
                return parsed.results ?? [];
            }

            return [];
        };

        const fetchData = async () => {
            setLoading(true);

            try {
                const response = await authenticatedRequest(
                    `${process.env.REACT_APP_BACKEND_URL}sale_acts/get_details/${actId}/`,
                    { method: 'GET', headers: { 'Content-Type': 'application/json' } }
                );

                if (response?.status !== 200) {
                    throw new Error(`HTTP ${response?.status}`);
                }

                const parsed = await response.json();

                if (parsed.cagent) {
                    parsed.cagentName = await resolveClientName(parsed.cagent);
                }

                const productList = [
                    ...(parsed.identified_products ?? []),
                    ...(parsed.unidentified_products ?? []),
                    ...(parsed.services ?? []),
                ];

                const sums = [];
                productList.forEach((product) => {
                    const existing = sums.find((item) => item.currency === product.currency);

                    if (existing) {
                        existing.amount += product.price;
                    } else {
                        sums.push({ currency: product.currency, amount: product.price });
                    }
                });

                const actPayments = await fetchPayments(parsed.id);

                if (cancelled) {
                    return;
                }

                setAct(parsed);
                setProducts(productList);
                setCharged(sums);
                setPayments(actPayments);
            } catch (error) {
                if (!cancelled) {
                    console.error('Не вдалося отримати акт продажу', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Акт недоступний',
                        life: 4000,
                    });
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [authenticatedRequest, actId]);

    const discounts = (act?.discount ?? []).filter((item) => Number(item.amount) > 0);

    if (loading) {
        return <EmptyState icon="pi pi-spin pi-spinner" text="Завантаження акту…" />;
    }

    if (!act) {
        return (
            <EmptyState
                icon="pi pi-exclamation-triangle"
                title="Акт не знайдено"
                text="Можливо, його видалено або немає доступу"
                action={
                    <Button
                        label="До історії"
                        icon="pi pi-arrow-left"
                        text
                        onClick={() => navigate('/salehistory')}
                    />
                }
            />
        );
    }

    return (
        <>
            <Toast ref={toast} position="top-center" />

            <div className="rc-stack">
                <div>
                    <h1 className="rc-page-title">Акт продажу №{act.id}</h1>
                    <p className="rc-page-subtitle">
                        {act.cagentName ?? 'Клієнта не зазначено'} · {displayDateTime(act.created_at)}
                    </p>
                </div>

                <div className="rc-split">
                    <div>
                        <Card title={`Товари та послуги (${products.length})`} flush>
                            {products.length === 0 ? (
                                <EmptyState icon="pi pi-box" text="Позицій немає" />
                            ) : (
                                products.map((item, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div className="rc-line-item" key={`product-${index}`}>
                                        <div className="rc-line-item__main">
                                            <div className="rc-line-item__name">
                                                {item.product ?? item.service}
                                            </div>
                                            <div className="rc-line-item__meta">
                                                <i className="pi pi-tag" />
                                                <span>
                                                    {item.identifier
                                                        ? `IMEI ${item.identifier}`
                                                        : `Кількість: ${item.quantity ?? 1}`}
                                                </span>
                                            </div>
                                        </div>
                                        <Money value={item.price} currency={item.currency} />
                                    </div>
                                ))
                            )}
                        </Card>
                    </div>

                    <div className="rc-stack">
                        <Card title="Загальні дані" flush>
                            <SummaryList>
                                <SummaryRow
                                    label="Клієнт"
                                    value={act.cagentName ?? 'Не зазначено'}
                                />
                                <SummaryRow
                                    label="Зареєстровано"
                                    sublabel={displayDateTime(act.created_at)}
                                    value={act.created_by}
                                />
                            </SummaryList>
                        </Card>

                        <Card title="Розрахунок" flush>
                            <SummaryList>
                                {charged.map((item) => (
                                    <SummaryRow
                                        key={`charged-${item.currency}`}
                                        label={`Нараховано, ${item.currency}`}
                                    >
                                        <Money value={item.amount} currency={item.currency} />
                                    </SummaryRow>
                                ))}
                                {discounts.map((item) => (
                                    <SummaryRow
                                        key={`discount-${item.currency}`}
                                        label={`Знижка, ${item.currency}`}
                                    >
                                        <Money
                                            value={-item.amount}
                                            currency={item.currency}
                                            tone="outcome"
                                        />
                                    </SummaryRow>
                                ))}
                                {(act.count_amount ?? []).map((item) => (
                                    <SummaryRow
                                        key={`payable-${item.currency}`}
                                        label={`До оплати, ${item.currency}`}
                                    >
                                        <Money value={item.amount} currency={item.currency} />
                                    </SummaryRow>
                                ))}
                                <SummaryRow label="Еквівалент у гривні" total>
                                    <Money value={act.amountUAH} currency="UAH" />
                                </SummaryRow>
                            </SummaryList>
                        </Card>

                        <Card title="Оплата" flush>
                            {payments.length === 0 ? (
                                <EmptyState icon="pi pi-credit-card" text="Платежів немає" />
                            ) : (
                                <SummaryList>
                                    {payments.map((payment, index) => (
                                        <SummaryRow
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`payment-${index}`}
                                            label={payment.currency}
                                            sublabel={displayDate(payment.date)}
                                        >
                                            <Money
                                                value={payment.distribution_sum}
                                                currency={payment.currency}
                                                tone="income"
                                            />
                                        </SummaryRow>
                                    ))}
                                </SummaryList>
                            )}
                        </Card>

                        <div className="rc-stack rc-stack--tight">
                            <Button
                                label="Друкувати чек"
                                icon="pi pi-print"
                                className="w-full"
                                onClick={() => printReceipt(act.id, toast)}
                            />
                            <Button
                                label="Оформити повернення"
                                icon="pi pi-replay"
                                severity="secondary"
                                outlined
                                className="w-full"
                                onClick={() =>
                                    navigate('/salereturn', { state: { actData: act } })
                                }
                            />
                            <Button
                                label="Назад до історії"
                                icon="pi pi-arrow-left"
                                text
                                className="w-full"
                                onClick={() => navigate('/salehistory')}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SaleDetails;
